import { CheckCircle2 } from "lucide-react";
import ConfirmDialog from "@/components/common/ConfirmDialog";

/**
 * Guards the one destructive-by-accident click every external portal offers:
 * ticking a checklist row complete. Internal agents (DealDetail) are
 * deliberately not wired to this — they work a much longer list and complete
 * rows constantly, so a dialog on every click would be friction without much
 * upside. External participants (client, lender, attorney, co-agent, vendor)
 * complete only a handful of rows across a whole deal, so the one extra click
 * is cheap and the accidental-completion it prevents is real — a client's own
 * checkbox in particular has no per-row "are you sure", just a single hero
 * control.
 *
 * Un-completing a row is intentionally NOT gated by this dialog — that
 * direction is already recoverable (the row just goes back to open), unlike
 * marking done, which notifies the agent and can advance the deal's phase.
 */
interface ConfirmCompleteDialogProps {
  open: boolean;
  taskTitle: string | null | undefined;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
}

export default function ConfirmCompleteDialog({ open, taskTitle, onOpenChange, onConfirm }: ConfirmCompleteDialogProps) {
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      tone="default"
      icon={CheckCircle2}
      title="Mark this as done?"
      description={
        <>
          &ldquo;{taskTitle}&rdquo; will be marked complete and your agent will be notified. You can undo this afterwards.
        </>
      }
      confirmLabel="Mark complete"
      busyLabel="Marking…"
      onConfirm={onConfirm}
      testId="confirm-complete-dialog"
    />
  );
}
